/**
 * TransactionItemRouter
 * Flutter source: wallet_screen.dart - build() switch statement
 *
 * Routes each transaction to the correct item component based on transactionType.
 */
import React from 'react';
import type { AppTransactionEntity } from '@/domain/entities/transaction/appTransactionEntity';
import { AppTransactionType } from '@/domain/enums/transaction/AppTransactionType';
import { GiftTransactionItem } from './GiftTransactionItem';
import { SubscriptionTransactionItem } from './SubscriptionTransactionItem';
import { WalletOperationItem } from './WalletOperationItem';
import { UnlockContentItem } from './UnlockContentItem';
import { PremiumPackageItem } from './PremiumPackageItem';
import { PremiumVideoItem } from './PremiumVideoItem';
import { VideoBoostItem } from './VideoBoostItem';
import { DonationItem } from './DonationItem';

interface TransactionItemRouterProps {
  transaction: AppTransactionEntity;
  currentUserId: string;
  onContentTap: (transaction: AppTransactionEntity) => void;
  /**
   * Admin variant of the wallet screen. Forwarded to each item component so the
   * five `userId`-targeting items (premium package, premium video, video boost,
   * wallet operation, donation) render the trailing admin avatar, and the
   * subscription item renders the admin-only title connector + peer admin avatar.
   */
  isFromAdmin?: boolean;
}

export const TransactionItemRouter: React.FC<TransactionItemRouterProps> = ({
  transaction,
  currentUserId,
  onContentTap,
  isFromAdmin = false,
}) => {
  switch (transaction.transactionType) {
    case AppTransactionType.gift:
      return (
        <GiftTransactionItem
          transaction={transaction}
          currentUserId={currentUserId}
          onContentTap={onContentTap}
        />
      );

    case AppTransactionType.subscription:
    case AppTransactionType.subscriptionRenewed:
      return (
        <SubscriptionTransactionItem
          transaction={transaction}
          currentUserId={currentUserId}
          isFromAdmin={isFromAdmin}
        />
      );

    case AppTransactionType.wallet:
      return (
        <WalletOperationItem
          transaction={transaction}
          currentUserId={currentUserId}
          isFromAdmin={isFromAdmin}
        />
      );

    case AppTransactionType.unlockContent:
      return (
        <UnlockContentItem
          transaction={transaction}
          currentUserId={currentUserId}
          onContentTap={onContentTap}
        />
      );

    case AppTransactionType.premiumPackage:
      return (
        <PremiumPackageItem
          transaction={transaction}
          currentUserId={currentUserId}
          isFromAdmin={isFromAdmin}
        />
      );

    case AppTransactionType.payPremiumVideo:
      return (
        <PremiumVideoItem
          transaction={transaction}
          currentUserId={currentUserId}
          onContentTap={onContentTap}
          isFromAdmin={isFromAdmin}
        />
      );

    case AppTransactionType.payBoostVideo:
      return (
        <VideoBoostItem
          transaction={transaction}
          currentUserId={currentUserId}
          onContentTap={onContentTap}
          isFromAdmin={isFromAdmin}
        />
      );

    default:
      return (
        <DonationItem
          transaction={transaction}
          currentUserId={currentUserId}
          isFromAdmin={isFromAdmin}
        />
      );
  }
};
